import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { SlashDataKeyItem } from "./data-key-registry";
import "./slash-command.css";

export interface SlashCommandListProps {
  items: SlashDataKeyItem[];
  command: (item: SlashDataKeyItem) => void;
}

/** Imperative handle the suggestion popup calls to forward keyboard events. */
export interface SlashCommandListRef {
  onKeyDown: (event: KeyboardEvent) => boolean;
}

/**
 * Keyboard-navigable list of data keys for the `/` slash command. Rendered into
 * a popup via `ReactRenderer` (outside the editor's React tree), so it stays
 * dependency-free and takes everything it needs through props.
 */
export const SlashCommandList = forwardRef<
  SlashCommandListRef,
  SlashCommandListProps
>(({ items, command }, ref) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Reset the highlight whenever the filtered result set changes.
  useEffect(() => {
    setActiveIndex(0);
  }, [items]);

  // Keep the highlighted item scrolled into view.
  useLayoutEffect(() => {
    itemRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const select = (index: number): void => {
    const item = items[index];
    if (item) {
      command(item);
    }
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: (event) => {
      if (event.key === "ArrowUp") {
        setActiveIndex((current) =>
          items.length === 0 ? 0 : (current + items.length - 1) % items.length
        );
        return true;
      }
      if (event.key === "ArrowDown") {
        setActiveIndex((current) =>
          items.length === 0 ? 0 : (current + 1) % items.length
        );
        return true;
      }
      if (event.key === "Enter") {
        select(activeIndex);
        return true;
      }
      return false;
    },
  }));

  if (items.length === 0) {
    return (
      <div className="tiptap-slash-command">
        <div className="tiptap-slash-command__empty">No matching data keys</div>
      </div>
    );
  }

  return (
    <div className="tiptap-slash-command" role="listbox">
      {items.map((item, index) => (
        <button
          key={item.key}
          type="button"
          ref={(element) => {
            itemRefs.current[index] = element;
          }}
          role="option"
          aria-selected={index === activeIndex}
          className={
            index === activeIndex
              ? "tiptap-slash-command__item is-active"
              : "tiptap-slash-command__item"
          }
          onMouseEnter={() => setActiveIndex(index)}
          onMouseDown={(event) => {
            // Prevent the editor from losing the selection before we insert.
            event.preventDefault();
            select(index);
          }}
        >
          <span className="tiptap-slash-command__label">
            {item.label ?? item.key}
          </span>
        </button>
      ))}
    </div>
  );
});

SlashCommandList.displayName = "SlashCommandList";

export default SlashCommandList;
